/* eslint-disable @typescript-eslint/indent */
import L from 'leaflet';
import 'leaflet.markercluster';
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { render } from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { FaMapMarkerAlt } from 'react-icons/fa';
import pinStyles from './RoutineMachine.module.scss';
import clusterStyles from './WaypointsClusterLayer.module.scss';

const MIDDLE_COLOR = '#1781A1';
const SMALL_THRESHOLD = 20;
const LARGE_THRESHOLD = 100;

const middleIcon = new L.DivIcon({
    html: ReactDOMServer.renderToString(
        <FaMapMarkerAlt style={{
            fontSize: '30px',
            color: MIDDLE_COLOR,
        }} />
    ),
    className: pinStyles.pin,
    iconAnchor: [15, 30],
    popupAnchor: [1, -34],
});

const buildClusterIcon = (cluster: any) => {
    const count = cluster.getChildCount();
    let bucket: string;
    let size: number;
    if (count < SMALL_THRESHOLD) {
        bucket = clusterStyles.clusterSmall;
        size = 26;
    } else if (count < LARGE_THRESHOLD) {
        bucket = clusterStyles.clusterMedium;
        size = 32;
    } else {
        bucket = clusterStyles.clusterLarge;
        size = 38;
    }
    return new L.DivIcon({
        html: `<div><span>${count}</span></div>`,
        className: `${clusterStyles.cluster} ${bucket}`,
        iconSize: L.point(size, size),
    });
};

interface Props {
    waypoints: L.LatLng[];
    renderInfo: (i: number) => JSX.Element;
}

const WaypointsClusterLayer: React.FC<Props> = ({ waypoints, renderInfo }) => {
    const map = useMap();

    useEffect(() => {
        if (!waypoints || waypoints.length <= 2) {
            return;
        }

        const group = (L as any).markerClusterGroup({
            chunkedLoading: true,
            maxClusterRadius: 50,
            disableClusteringAtZoom: 18,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            iconCreateFunction: buildClusterIcon,
        });

        for (let i = 1; i < waypoints.length - 1; i++) {
            const marker = L.marker(waypoints[i], { icon: middleIcon })
                .bindPopup(() => {
                    const div = document.createElement('div');
                    render(renderInfo(i), div);
                    return div.innerHTML;
                }, { minWidth: 230, closeButton: false });
            group.addLayer(marker);
        }

        map.addLayer(group);

        return () => {
            map.removeLayer(group);
        };
    }, [map, waypoints, renderInfo]);

    return null;
};

export default WaypointsClusterLayer;
